import {
  buildEmailIssueLookup,
  collectEmailIssuesForPeople,
  normalizeEmailAddress,
  summarizeEmailIssueGroups,
} from './emailHealthSignals';

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const REACH_DADS_STAGE_PRIORITY = {
  webinar_attended: 0,
  webinar_registered: 1,
  webinar_invited: 2,
  prospect: 3,
  not_now: 4,
};

const REACH_DADS_PIPELINE_STAGES = new Set([
  'prospect',
  'webinar_invited',
  'webinar_registered',
  'webinar_attended',
]);

const formatLabel = (value) => {
  if (!value) return '';
  return String(value)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  if (!value) return 'date pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'date pending';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isGuestPassExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return getTimeValue(expiresAt) < Date.now();
};

const getFounding40Remaining = (founding40Status) => {
  if (!founding40Status) return null;
  return Math.max(0, (founding40Status.max_members || 0) - (founding40Status.current_count || 0));
};

const getFounding40Mode = (siteSettings = null) =>
  siteSettings?.founding_40_public_mode === 'webinar' ? 'webinar' : 'join';

const getDadRelationshipStage = (member = {}) => {
  const storedStage = (member.relationship_stage || '').trim();
  if (storedStage) return storedStage;
  const tier = (member.tier || '').trim().toLowerCase();
  if (tier === 'prospect') return 'prospect';
  if (tier === 'member') return 'member';
  return '';
};

const getProspectCount = (members = []) =>
  members.filter((member) => {
    const relationshipStage = getDadRelationshipStage(member).toLowerCase();
    const tier = (member?.tier || '').trim().toLowerCase();
    return relationshipStage === 'prospect' || tier === 'prospect';
  }).length;

const getWarmProspects = (members = []) =>
  members
    .filter((member) => {
      const tier = (member?.tier || '').trim().toLowerCase();
      if (tier === 'guide_partner') return false;

      const stage = getDadRelationshipStage(member);
      if (!REACH_DADS_PIPELINE_STAGES.has(stage)) return false;

      const warmth = (member?.warmth || '').trim().toLowerCase();
      const source = (member?.source || '').trim().toLowerCase();
      return warmth === 'warm' || source === 'warm_personal' || source === 'referral';
    })
    .sort((a, b) => {
      const stageDiff = (REACH_DADS_STAGE_PRIORITY[getDadRelationshipStage(a)] ?? 99)
        - (REACH_DADS_STAGE_PRIORITY[getDadRelationshipStage(b)] ?? 99);
      if (stageDiff !== 0) return stageDiff;

      return getTimeValue(b.updated_at || b.created_at) - getTimeValue(a.updated_at || a.created_at);
    });

const getFoundingFortyPipeline = (members = []) =>
  members.filter((member) => {
    const stage = getDadRelationshipStage(member);
    return stage === 'webinar_attended';
  });

const getOpenGuestPasses = (sentGuestPasses = []) =>
  sentGuestPasses
    .filter((pass) => !pass?.redeemed && !isGuestPassExpired(pass?.expires_at))
    .sort((a, b) => getTimeValue(b.created_at) - getTimeValue(a.created_at));

const getReachDadEmailIssues = (members = [], emailDeliveryIssues = []) => {
  const issueLookup = buildEmailIssueLookup(emailDeliveryIssues);

  return collectEmailIssuesForPeople(
    members.filter((member) => {
      const tier = (member?.tier || '').trim().toLowerCase();
      if (tier === 'guide_partner') return false;
      return REACH_DADS_PIPELINE_STAGES.has(getDadRelationshipStage(member));
    }),
    (member) => issueLookup.get(normalizeEmailAddress(member?.email))
  );
};

const buildReachDadsAction = (label, tab, roomId = 'reach-dads', toolContext = null) => ({
  label,
  tab,
  roomId,
  toolContext,
});

export function buildReachDadsRoomContext({
  room,
  founding40Status = null,
  members = [],
  sentGuestPasses = [],
  emailDeliveryIssues = [],
  siteSettings = null,
  primaryAction,
  secondaryAction,
}) {
  const founding40Mode = getFounding40Mode(siteSettings);
  const joinFirstMode = founding40Mode !== 'webinar';
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const prospectCount = getProspectCount(members);
  const guestPassCount = sentGuestPasses.length;
  const openGuestPasses = getOpenGuestPasses(sentGuestPasses);
  const warmProspects = getWarmProspects(members);
  const foundingFortyPipeline = getFoundingFortyPipeline(members);
  const reachDadEmailIssues = getReachDadEmailIssues(members, emailDeliveryIssues);
  const reachDadEmailIssueGroups = summarizeEmailIssueGroups(reachDadEmailIssues);

  const warmNowItems = [
    ...warmProspects.slice(0, 3).map((member) => ({
      id: member.user_id || member.id || member.email || member.name || 'warm-dad',
      eyebrow: 'Warm prospect',
      title: member?.name || member?.email || 'Unnamed prospect',
      detail: [
        formatLabel(getDadRelationshipStage(member)),
        formatLabel(member?.source || member?.warmth),
        member?.updated_at || member?.created_at ? `updated ${formatDate(member.updated_at || member.created_at)}` : null,
      ].filter(Boolean).join(' · '),
      tone: 'attention',
      location: 'In Prospects',
      action: buildReachDadsAction('Open Prospects', 'members'),
    })),
    ...openGuestPasses.slice(0, 2).map((pass) => ({
      id: pass.pass_id || `${pass.recipient_email || 'guest-pass'}-${pass.code || 'code'}`,
      eyebrow: 'Guest pass open',
      title: pass?.recipient_name || pass?.recipient_email || 'Unnamed guest pass',
      detail: [
        pass?.created_at ? `sent ${formatDate(pass.created_at)}` : null,
        pass?.expires_at ? `expires ${formatDate(pass.expires_at)}` : 'no expiry yet',
      ].filter(Boolean).join(' · '),
      tone: 'next',
      location: 'In Guest Passes',
      action: buildReachDadsAction('Open Guest Passes', 'guestpasses'),
    })),
  ].slice(0, 4);

  const needsYourMoveItems = [
    ...reachDadEmailIssues.slice(0, 2).map(({ person, issue, meta }) => ({
      id: `reach-email-${person?.user_id || person?.email || issue?.email || meta.label}`,
      eyebrow: meta.label,
      title: person?.name || person?.email || 'Unnamed prospect',
      detail: `${meta.actionLabel} · ${issue?.email || normalizeEmailAddress(person?.email)}`,
      tone: meta.tone,
      location: 'In Prospects',
      action: buildReachDadsAction('Open Prospects', 'members'),
    })),
    ...foundingFortyPipeline.slice(0, 2).map((member) => ({
      id: `reach-followup-${member.user_id || member.id || member.email || member.name || 'dad'}`,
      eyebrow: 'Ready for follow-up',
      title: member?.name || member?.email || 'Unnamed dad',
      detail: [
        formatLabel(getDadRelationshipStage(member)),
        formatLabel(member?.source || member?.warmth),
        member?.updated_at || member?.created_at ? `last touched ${formatDate(member.updated_at || member.created_at)}` : null,
      ].filter(Boolean).join(' · '),
      tone: 'next',
      location: 'In Prospects or Email',
      action: buildReachDadsAction('Open Email', 'email'),
    })),
  ].slice(0, 4);

  const suggestedActions = [];

  if (reachDadEmailIssues.length > 0) {
    suggestedActions.push({
      tone: 'attention',
      eyebrow: 'Do first',
      title: joinFirstMode
        ? `Correct ${pluralize(reachDadEmailIssues.length, 'dad record')} before trusting the next move`
        : `Correct ${pluralize(reachDadEmailIssues.length, 'dad record')} before assuming outreach landed`,
      detail: reachDadEmailIssueGroups.doNotEmail > 0
        ? `${pluralize(reachDadEmailIssueGroups.doNotEmail, 'complaint')} need manual care before another send.`
        : 'Clean up bounced or suppressed addresses before the next relationship move.',
      location: 'In Prospects',
      action: buildReachDadsAction('Open Prospects', 'members'),
    });
  }

  if (joinFirstMode && foundingFortyPipeline.length > 0) {
    suggestedActions.push({
      tone: 'attention',
      eyebrow: suggestedActions.length > 0 ? 'Then' : 'Do first',
      title: `Follow up with ${pluralize(foundingFortyPipeline.length, 'high-intent dad')}`,
      detail: 'These dads are already close to a real Founding 40 decision and need a clear personal next step.',
      location: 'In Email',
      action: buildReachDadsAction('Open Email', 'email'),
    });
  }

  if (warmProspects.length > 0) {
    suggestedActions.push({
      tone: joinFirstMode ? 'next' : 'attention',
      eyebrow: suggestedActions.length > 0 ? (joinFirstMode ? 'Keep moving' : 'Then') : 'Do first',
      title: `Review ${pluralize(warmProspects.length, 'warm prospect')}`,
      detail: joinFirstMode
        ? 'Start with the warmest names in the pipeline and decide the next personal move toward Founding 40.'
        : 'Start with the warmest names in the pipeline and decide who should hear from you next.',
      location: 'In Prospects',
      action: buildReachDadsAction('Open Prospects', 'members'),
    });
  }

  if (!joinFirstMode && foundingFortyPipeline.length > 0) {
    suggestedActions.push({
      tone: 'next',
      eyebrow: suggestedActions.length > 0 ? 'Keep moving' : 'Do first',
      title: `Follow up with ${pluralize(foundingFortyPipeline.length, 'high-intent dad')}`,
      detail: 'These dads already showed stronger interest and need a clear personal next step.',
      location: 'In Email',
      action: buildReachDadsAction('Open Email', 'email'),
    });
  }

  if (!suggestedActions.length && openGuestPasses.length > 0) {
    suggestedActions.push({
      tone: 'next',
      eyebrow: 'Do first',
      title: `Check ${pluralize(openGuestPasses.length, 'open guest pass')}`,
      detail: 'A guest pass is still sitting in the lane and may need a nudge or a decision about what happens next.',
      location: 'In Guest Passes',
      action: buildReachDadsAction('Open Guest Passes', 'guestpasses'),
    });
  }

  if (!suggestedActions.length) {
    suggestedActions.push({
      tone: 'steady',
      eyebrow: 'Quiet',
      title: 'Keep the dad pipeline warm and ready for the next real move',
      detail: 'Nothing is pressing right now, so this is a good moment to review the queue without forcing extra outreach.',
      location: 'In Prospects',
      action: buildReachDadsAction('Open Prospects', 'members'),
    });
  }

  const statusTone = reachDadEmailIssues.length > 0
    ? 'attention'
    : warmProspects.length > 0 || foundingFortyPipeline.length > 0
      ? 'next'
      : openGuestPasses.length > 0
        ? 'next'
        : prospectCount > 0 || (remainingFounding40 || 0) > 0
          ? 'attention'
          : 'steady';

  const statusLabel = reachDadEmailIssues.length > 0
    ? 'Blocked outreach'
    : warmProspects.length > 0 || foundingFortyPipeline.length > 0
      ? (joinFirstMode ? 'Needs next move' : 'Needs follow-up')
      : openGuestPasses.length > 0
        ? 'In motion'
        : 'Steady';

  return {
    id: room.id,
    variant: 'reach-dads',
    statusTone,
    statusLabel,
    startTitle: reachDadEmailIssues.length > 0
      ? (joinFirstMode ? 'Clear blocked outreach before trusting the next move' : 'Clear blocked outreach before trusting the pipeline')
      : joinFirstMode && foundingFortyPipeline.length > 0
        ? `Move ${pluralize(foundingFortyPipeline.length, 'high-intent dad')} toward Founding 40`
        : warmProspects.length > 0
          ? (joinFirstMode
            ? `Review ${pluralize(warmProspects.length, 'warm prospect')} and decide the next personal move`
            : `Review ${pluralize(warmProspects.length, 'warm prospect')} and decide who should hear from you next`)
          : 'Keep the next relationship move clear and personal',
    startDetail: joinFirstMode
      ? 'This room should help you see who looks warm, what is stuck, and which prospect needs a clear personal next step toward Founding 40 across Prospects, Guest Passes, and Email.'
      : 'This room should help you see who looks warm, what is stuck, and which relationship move should happen next across Prospects, Guest Passes, and Email.',
    signals: [
      {
        tone: (remainingFounding40 || 0) > 0 ? 'attention' : 'steady',
        text: remainingFounding40 !== null
          ? `${pluralize(remainingFounding40, 'Founding 40 spot')} still open`
          : 'Founding 40 status will surface here once the count is loaded.',
      },
      {
        tone: warmProspects.length > 0 ? 'next' : 'steady',
        text: warmProspects.length > 0
          ? `${pluralize(warmProspects.length, 'warm prospect')} are in the current pipeline`
          : prospectCount > 0
            ? `${pluralize(prospectCount, 'visible prospect')} are in view, but none are clearly warm yet`
            : 'No visible prospect movement is surfacing right now.',
      },
      {
        tone: reachDadEmailIssues.length > 0 ? 'attention' : openGuestPasses.length > 0 ? 'next' : 'steady',
        text: reachDadEmailIssues.length > 0
          ? `${pluralize(reachDadEmailIssues.length, 'dad')} may not have actually received the invite because of email delivery issues`
          : openGuestPasses.length > 0
            ? `${pluralize(openGuestPasses.length, 'guest pass')} are still open`
            : `${pluralize(guestPassCount, 'guest pass')} have been sent from admin so far`,
      },
    ],
    metrics: [
      { label: joinFirstMode ? 'Pipeline dads' : 'Visible prospects', value: prospectCount, detail: joinFirstMode ? 'Dads still somewhere in the current movement path' : 'Dads still somewhere in the pipeline', tone: prospectCount > 0 ? 'attention' : 'steady' },
      { label: joinFirstMode ? 'High-intent dads' : 'Warm prospects', value: joinFirstMode ? foundingFortyPipeline.length : warmProspects.length, detail: joinFirstMode ? 'Dads closest to a real Founding 40 move' : 'Prospects with relational momentum', tone: (joinFirstMode ? foundingFortyPipeline.length : warmProspects.length) > 0 ? 'next' : 'steady' },
      reachDadEmailIssues.length > 0
        ? { label: 'Email issues', value: reachDadEmailIssues.length, detail: reachDadEmailIssueGroups.doNotEmail > 0 ? `${pluralize(reachDadEmailIssueGroups.doNotEmail, 'complaint')} need manual care` : 'Fix these before assuming outreach landed', tone: 'attention' }
        : { label: 'Founding 40 open', value: remainingFounding40 ?? '—', detail: 'Spots still to fill', tone: (remainingFounding40 || 0) > 0 ? 'attention' : 'steady' },
    ],
    warmNowItems,
    needsYourMoveItems,
    suggestedActions,
    primaryAction,
    secondaryAction,
  };
}
